import type { ReactNode } from 'react';

interface SchemaOrgProps {
  type: 'Organization' | 'WebSite' | 'WebPage' | 'BreadcrumbList' | 'Article' | 'FAQPage' | 'ItemList';
  data: Record<string, unknown>;
}

export default function SchemaOrg({ type, data }: SchemaOrgProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': type,
    ...data,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function OrganizationSchema(): ReactNode {
  return (
    <SchemaOrg
      type="Organization"
      data={{
        name: '黄瓜影视在线看',
        url: 'https://huanggua.tv',
        description: '黄瓜影视在线看，海量高清影视资源免费畅享，涵盖热门电影、电视剧、综艺、动漫等全类型内容。',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-888-6666',
          contactType: 'customer service',
          email: 'contact@huanggua.tv',
          availableLanguage: ['Chinese'],
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          streetAddress: '建国路88号SOHO现代城A座',
          addressCountry: 'CN',
        },
      }}
    />
  );
}

export function WebSiteSchema(): ReactNode {
  return (
    <SchemaOrg
      type="WebSite"
      data={{
        name: '黄瓜影视在线看',
        url: 'https://huanggua.tv',
        description: '免费高清影视在线观看平台',
        inLanguage: 'zh-CN',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://huanggua.tv/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      }}
    />
  );
}

export function WebPageSchema({ title, description, url }: { title: string; description: string; url: string }): ReactNode {
  return (
    <SchemaOrg
      type="WebPage"
      data={{
        name: title,
        description,
        url,
        inLanguage: 'zh-CN',
        isPartOf: {
          '@type': 'WebSite',
          name: '黄瓜影视在线看',
          url: 'https://huanggua.tv',
        },
      }}
    />
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbListSchema({ items }: { items: BreadcrumbItem[] }): ReactNode {
  return (
    <SchemaOrg
      type="BreadcrumbList"
      data={{
        itemListElement: items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      }}
    />
  );
}

interface ArticleSchemaProps {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  url: string;
}

export function ArticleSchema({ title, description, image, datePublished, dateModified, author, url }: ArticleSchemaProps): ReactNode {
  return (
    <SchemaOrg
      type="Article"
      data={{
        headline: title,
        description,
        image,
        datePublished,
        dateModified,
        author: {
          '@type': 'Person',
          name: author,
        },
        publisher: {
          '@type': 'Organization',
          name: '黄瓜影视在线看',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': url,
        },
      }}
    />
  );
}

interface FAQSchemaProps {
  questions: { question: string; answer: string }[];
}

export function FAQPageSchema({ questions }: FAQSchemaProps): ReactNode {
  return (
    <SchemaOrg
      type="FAQPage"
      data={{
        mainEntity: questions.map(q => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      }}
    />
  );
}

export function ItemListSchema({ items }: { items: { name: string; url: string; position: number }[] }): ReactNode {
  return (
    <SchemaOrg
      type="ItemList"
      data={{
        itemListElement: items.map(item => ({
          '@type': 'ListItem',
          position: item.position,
          name: item.name,
          url: item.url,
        })),
      }}
    />
  );
}